import {
  type Query as TCubeQuery,
  type TimeDimensionGranularity,
} from "@cubejs-client/core"
import { UseCubeQueryOptions } from "@cubejs-client/react"
import { useEffect, useMemo, useRef, useState } from "react"
import { useAppDispatch, useAppSelector } from "~/ui-rtk/app/hooks"
import { SortingState } from "@tanstack/react-table"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import {
  GranularityMapping,
  MetricsMapping,
  generalPostProcess,
} from "~/ui-rtk/constants/metrics-mapping"
import { assetsApi } from "~/ui-rtk/api/assetsApi"
import { cacheAssetUrl } from "~/ui-rtk/app/slices/asset-cache.slice"
import { mapMediaChannelToSourceType } from "~/ui-rtk/components/atoms/MarathonImage/helpers"
import { AggregationType } from "~/ui-rtk/constants/charts"
import { getCubeCacheKey } from "~/ui-rtk/hooks/metrics-cache"
import {
  addSorting,
  addFilters,
  addPagination,
  getPivotData,
  polyfillCubeQueryWithTimeDimension,
} from "~/ui-rtk/utils/cube"
import { TCubeFilterOptions } from "~/ui-rtk/shared/types/charts"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import useDate from "~/ui-rtk/hooks/date"
import useWeekGranularity from "~/ui-rtk/hooks/week-granularity"

const requests: Record<string, TCubeQuery | TCubeQuery[]> = {}

const dayjs = useDate()

type TConnectOptions = {
  chartId: string
  granularity: AggregationType
  dateRange: [Date, Date]
  page: number
  itemsPerPage: number
  enablePagination: boolean
  disableGranularity?: boolean
  prefixEnabled?: boolean
  cubeFilters?: TCubeFilterOptions
  sorting?: SortingState
  extraSorting?: Array<{ id: string; desc: boolean }>
  cubeQueryParams?: Record<string, string>
}

export default function useConnect({
  chartId,
  granularity,
  dateRange,
  page,
  itemsPerPage,
  cubeFilters,
  sorting,
  extraSorting,
  enablePagination,
  disableGranularity = false,
  prefixEnabled = false,
  cubeQueryParams = {},
}: TConnectOptions) {
  const $granularity: AggregationType | undefined = !disableGranularity
    ? granularity
    : undefined

  const currentCompany = useCurrentCompany()
  if (!currentCompany) {
    throw new Error("No current company")
  }

  const dateRangeOfStart: [Date, Date] = [
    dayjs(dateRange[0]).startOf("day").toDate(),
    dayjs(dateRange[1]).startOf("day").toDate(),
  ]

  const { id: companyId } = currentCompany

  // Weekly queries request the company's reporting week granularity (e.g.
  // `week_thursday`) so the request, response keys, and client-side pivot all
  // agree. Baked into the request-dedupe key below: it resolves from an async
  // financials fetch, and a stale cached query would keep sending `week`.
  const weekGranularity = useWeekGranularity()

  const key = `${getCubeCacheKey({
    companyId,
    chartId,
    dateRange: dateRangeOfStart,
    page,
    itemsPerPage,
    cubeFilters,
    sorting: sorting as Record<string, unknown>[] | undefined,
    granularity: $granularity,
    cubeQueryParams,
  })}-wg${weekGranularity}`

  const resolvedGranularity =
    granularity === AggregationType.WEEKLY
      ? (weekGranularity as TimeDimensionGranularity)
      : GranularityMapping[granularity]
  const granularityValue = !disableGranularity ? resolvedGranularity : undefined
  const { q, getCubePrefix } = MetricsMapping[chartId]
  const cubeQuery = typeof q === "function" ? q(cubeQueryParams) : q

  const polyfillTimeDimensionPayload = {
    granularity: granularityValue,
    dateRange: dateRangeOfStart,
  }

  let cubePrefix: string | undefined
  if (prefixEnabled && getCubePrefix) {
    cubePrefix = getCubePrefix({ granularity: GranularityMapping[granularity] })
  }

  const query = useMemo(() => {
    let query: TCubeQuery | TCubeQuery[]
    if (requests[key]) {
      query = requests[key]
    } else {
      const offset = page * itemsPerPage

      if (Array.isArray(cubeQuery)) {
        query = cubeQuery
          .map(cq =>
            polyfillCubeQueryWithTimeDimension(
              cq,
              polyfillTimeDimensionPayload,
            ),
          )
          .map(cq => addSorting(cq, sorting, cubePrefix, extraSorting))
        if (enablePagination) {
          query = query.map(cq => addPagination(cq, itemsPerPage, offset))
        }
        if (cubeFilters) {
          query = query.map(cq => addFilters(cq, cubeFilters, cubePrefix))
        }
      } else {
        query = addSorting(
          polyfillCubeQueryWithTimeDimension(
            cubeQuery,
            polyfillTimeDimensionPayload,
          ),
          sorting,
          cubePrefix,
          extraSorting,
        )
        if (enablePagination) {
          query = addPagination(query, itemsPerPage, offset)
        }

        if (cubeFilters) {
          query = addFilters(query, cubeFilters, cubePrefix)
        }
      }
    }

    requests[key] = query

    return query
  }, [key, chartId, enablePagination, itemsPerPage, page, cubeFilters])

  const queryOptions: UseCubeQueryOptions = useMemo(
    () => ({
      skip: MetricsMapping[chartId].disabled,
    }),
    [chartId],
  )

  const { resultSet, progress, isLoading } = useCubeQuery(query, queryOptions)

  const dispatch = useAppDispatch()
  // Read the asset-cache slice so we can skip already-resolved URLs when the
  // dataSource changes (filter, pagination, etc.) — `resolveAssets` is a
  // mutation, so RTK Query won't dedupe across calls for us. Mirrored into a
  // ref so the effect can read the latest snapshot WITHOUT taking it as a
  // dep (which would feedback-loop: effect runs → cache populates → urls
  // change → effect re-runs).
  const cachedAssetUrls = useAppSelector(state => state.assetCacheSlice.urls)
  const cachedAssetUrlsRef = useRef(cachedAssetUrls)
  cachedAssetUrlsRef.current = cachedAssetUrls

  // Cube data pipeline: postProcess is now synchronous for every chart
  // (tag/asset enrichment moved out — see comment block below). Asset
  // resolution fires fire-and-forget AFTER the cube cache is populated, so it
  // doesn't block first paint. Tag assignments are subscribed live in
  // TableVisualization, completely outside of this hook.
  const [processed, setProcessed] = useState<{
    dataSource?: Array<Record<string, string | number | boolean>>
    columns?: Array<{ key: string; [key: string]: unknown }>
  }>({})

  useEffect(() => {
    if (!resultSet) return undefined
    let cancelled = false

    const run = async () => {
      const baseColumns = resultSet.tableColumns()
      const { postProcess } = MetricsMapping[chartId]
      const pivotData = getPivotData(resultSet, query)

      let nextDataSource:
        | Array<Record<string, string | number | boolean>>
        | undefined
      let nextColumns: Array<{ key: string; [key: string]: unknown }> =
        baseColumns

      if (postProcess) {
        // dispatch + cachedAssetUrls are passed so a postProcess can do its
        // own bulk asset resolution and merge URLs onto rows before render
        // (see resolveRowAssets in utils/resolve-row-assets.ts).
        const result = await Promise.resolve(
          postProcess(pivotData, {
            ...cubeQueryParams,
            dispatch,
            cachedAssetUrls: cachedAssetUrlsRef.current,
          }),
        )
        nextDataSource = result[0] as Array<
          Record<string, string | number | boolean>
        >
        if (result[1]) {
          nextColumns = result[1] as Array<{
            key: string
            [key: string]: unknown
          }>
        }
      } else {
        nextDataSource = generalPostProcess(
          pivotData as Array<Record<string, string | number>>,
          baseColumns as unknown as Array<{
            key: string
            dataIndex?: string
            type?: string
            [key: string]: string | undefined
          }>,
        )
      }

      if (cancelled) return
      delete requests[key]
      setProcessed({ dataSource: nextDataSource, columns: nextColumns })
    }

    void run()

    return () => {
      cancelled = true
    }
    // We intentionally don't include `cubeQueryParams` (caller's reference is
    // unstable). The cube query itself re-fires when relevant params change,
    // so resultSet identity is enough to retrigger postProcess.
  }, [resultSet, chartId, key])

  // Fire-and-forget asset resolution, AFTER the cube cache is populated. This
  // used to live inside postProcess and was blocking first paint by 2-5s.
  // MarathonImage reads from the asset-cache slice this populates.
  useEffect(() => {
    const rows = processed.dataSource
    if (!rows?.length) return
    const cfg = MetricsMapping[chartId].assetFields
    if (!cfg) return
    const seen = new Set<string>()
    const assets: Array<{
      sourceType: string
      externalRefId: string
      url?: string
    }> = []
    for (const row of rows) {
      const r = row as Record<string, unknown>
      const rawSourceType = r[cfg.sourceTypeKey]
      const refId = r[cfg.externalRefIdKey]
      if (!rawSourceType || !refId) continue
      const sourceType = mapMediaChannelToSourceType(String(rawSourceType))
      const key = `${sourceType}:${refId}`
      // Skip URLs already in the asset-cache slice. Without this, every
      // dataSource change (filter, pagination, refetch) re-submits the full
      // visible set to the backend. Read via ref so the effect doesn't depend
      // on the urls object identity.
      if (seen.has(key) || cachedAssetUrlsRef.current[key]) continue
      seen.add(key)
      const url = cfg.urlKey ? (r[cfg.urlKey] as string | undefined) : undefined
      assets.push({
        sourceType,
        externalRefId: String(refId),
        ...(url ? { url } : {}),
      })
    }
    if (assets.length === 0) return undefined
    const sub = dispatch(
      assetsApi.endpoints.resolveAssets.initiate({
        assets: assets as Parameters<
          typeof assetsApi.endpoints.resolveAssets.initiate
        >[0]["assets"],
      }),
    )
    let cancelled = false
    sub
      .unwrap()
      .then(result => {
        if (cancelled) return
        for (const [cacheKey, asset] of Object.entries(result.results)) {
          if (!asset) continue
          let resolvedUrl: string | null = null
          if (asset.syncStatus === "SYNCED") {
            resolvedUrl = asset.url || asset.thumbnailUrl || null
          }
          if (!resolvedUrl) {
            const match = assets.find(
              a => `${a.sourceType}:${a.externalRefId}` === cacheKey,
            )
            resolvedUrl = match?.url || null
          }
          if (resolvedUrl) {
            dispatch(cacheAssetUrl({ key: cacheKey, url: resolvedUrl }))
          }
        }
      })
      .catch(() => undefined)
    // Cleanup fires on unmount AND every time the effect re-runs (deps change),
    // covering both the in-flight unmount case and the normal post-resolution
    // case. Without this, a `.finally(() => sub.reset())` on the promise leaks
    // the mutation cache entry whenever the component unmounts before the
    // network request completes.
    return () => {
      cancelled = true
      sub.reset()
    }
  }, [processed.dataSource, chartId, dispatch])

  let totalPages = 1
  if (resultSet && itemsPerPage) {
    totalPages = Math.ceil((resultSet.totalRows() ?? 0) / itemsPerPage)
  }

  // Cube data only. Tag assignments are handled at the TableVisualization
  // level via a live RTK Query subscription — they can't live in this hook
  // because Chart unmounts <ChartDataLoader> as soon as the cube cache is
  // populated, which would tear down any subscription we put here.
  const dataSourceMemo = useMemo(() => {
    if (MetricsMapping[chartId].disabled) return []
    return processed.dataSource
  }, [chartId, processed.dataSource])

  const columnsMemo = useMemo(() => processed.columns, [processed.columns])

  return {
    dataSource: dataSourceMemo,
    columns: columnsMemo,
    progress,
    totalPages,
    isLoading: MetricsMapping[chartId].disabled ? false : isLoading,
    granularity: granularityValue,
  }
}
